import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { type JSX } from "react";
import { BTX_DEPENDENT_RATE_BASIS } from "@platform/feature-flags";
import { FeatureFlagBoundary } from "@platform/fe-utils";

import { RateScheduleFormData } from "../../types/models";
import {
  isBasicLifeCoverageType,
  isLifeCoverageType,
} from "../../utils/rate-helpers";

import { AdndRateConfigSection } from "./AdndRateConfigSection";
import { DependentRateUnitSection } from "./DependentRateUnitSection";
import { RateUnitSection } from "./RateUnitSection";

interface FlatRateConfigSectionProps {
  formData: RateScheduleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RateScheduleFormData>>;
  coverageType?: string;
}

/**
 * Flat-rate-specific form controls: dependent rates, combined/separate,
 * volume/unit, unit selection, and AD&D configuration.
 * Extracted to reduce RatesTab cyclomatic complexity.
 */
export function FlatRateConfigSection({
  formData,
  setFormData,
  coverageType,
}: FlatRateConfigSectionProps): JSX.Element {
  const handleIncludeDependentRatesChange = (include: boolean): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      includeDependentRates: include,
      // Default to combined when toggling on, reset when toggling off
      dependentRateType: include ? "combined" : undefined,
    }));
  };

  const handleDependentRateTypeChange = (
    value: "combined" | "separate",
  ): void => {
    setFormData((prev: RateScheduleFormData) => ({
      ...prev,
      dependentRateType: value,
    }));
  };

  const includeDependentRatesValue =
    formData.includeDependentRates === true ? "yes" : "no";

  return (
    <>
      {/* ─── Are Dependent Rates Included? ─── */}
      <FormControl component="fieldset">
        <FormLabel component="legend">Are Dependent Rates Included?</FormLabel>
        <RadioGroup
          row
          value={includeDependentRatesValue}
          onChange={(e) =>
            handleIncludeDependentRatesChange(e.target.value === "yes")
          }
        >
          <FormControlLabel
            value="yes"
            control={<Radio color="brandPrimary" />}
            label="Yes"
          />
          <FormControlLabel
            value="no"
            control={<Radio color="brandPrimary" />}
            label="No"
          />
        </RadioGroup>
      </FormControl>

      {/* ─── Dependents combined or separate? (only when dependents included) ─── */}
      {formData.includeDependentRates && (
        <FormControl component="fieldset">
          <FormLabel component="legend">
            Are dependents combined or separate?
          </FormLabel>
          <RadioGroup
            row
            value={formData.dependentRateType || ""}
            onChange={(e) =>
              handleDependentRateTypeChange(
                e.target.value as "combined" | "separate",
              )
            }
          >
            <FormControlLabel
              value="combined"
              control={<Radio color="brandPrimary" />}
              label="Combined"
            />
            <FormControlLabel
              value="separate"
              control={<Radio color="brandPrimary" />}
              label="Separate"
            />
          </RadioGroup>
        </FormControl>
      )}

      {/* ─── Rates based on volume or unit? ─── */}
      <RateUnitSection
        formData={formData}
        setFormData={setFormData}
        coverageType={coverageType}
      />

      {/* ─── Dependent rates based on volume or unit? (Basic Life only, when dependents included) ─── */}
      {formData.includeDependentRates &&
        isBasicLifeCoverageType(coverageType) && (
          <FeatureFlagBoundary flag={BTX_DEPENDENT_RATE_BASIS}>
            <DependentRateUnitSection
              formData={formData}
              setFormData={setFormData}
            />
          </FeatureFlagBoundary>
        )}

      {/* ─── AD&D Rate Configuration (life products only) ─── */}
      {isLifeCoverageType(coverageType) && (
        <AdndRateConfigSection
          formData={formData}
          setFormData={setFormData}
          coverageType={coverageType}
        />
      )}
    </>
  );
}
